import { useState, useEffect } from 'react';
import { getProposals, getProposalsCount, Proposal } from '../lib/proposals';

export function useProposals(businessId?: string) {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const loadProposals = async (reset: boolean = true) => {
    setError(null);

    if (!businessId) {
      setLoading(false);
      setProposals([]);
      setHasMore(false);
      setTotalCount(0);
      return;
    }

    try {
      if (reset) {
        setLoading(true);
        setProposals([]);
      } else {
        setLoadingMore(true);
      }

      const offset = reset ? 0 : proposals.length;
      const limit = 30;

      // Load proposals and total count
      const [data, count] = await Promise.all([
        getProposals(businessId, { limit, offset }).catch(err => {
          console.error('Error in getProposals:', err);
          throw new Error('Unable to load proposals. Please try again.');
        }),
        reset ? getProposalsCount(businessId) : Promise.resolve(totalCount)
      ]);

      if (reset) {
        setProposals(data);
        setTotalCount(count);
      } else {
        setProposals(prev => [...prev, ...data]);
      }

      // Check if there are more items to load
      const newTotal = reset ? data.length : proposals.length + data.length;
      setHasMore(newTotal < count);

    } catch (err) {
      console.error('Error loading proposals:', err);
      setError(err instanceof Error ? err.message : 'Failed to load proposals');
      if (reset) {
        setProposals([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      loadProposals(false);
    }
  };

  useEffect(() => {
    loadProposals(true);
  }, [businessId]);

  return { 
    proposals, 
    loading, 
    loadingMore,
    error, 
    hasMore,
    totalCount,
    refetch: () => loadProposals(true),
    loadMore
  };
} 